import { sessionStorage } from "@web-monorepo/safe-browser-storage";

const PENDING_FAVORITE_KEY = "marketplacePendingFavorite";

/**
 * Expiration time for pending favorite actions.
 *
 * Set to 15 minutes to accommodate complex login flows including:
 * - Two-factor authentication (2FA)
 * - Password reset flows
 * - Email verification
 * - Social login redirects
 *
 * Note: sessionStorage automatically clears on tab close, so this expiration
 * primarily guards against abandoned login attempts within the same session.
 * Users who close their tab will lose the pending action regardless of this timeout.
 */
const EXPIRATION_TIME_MS = 15 * 60 * 1000; // 15 minutes

export interface PendingTutorFavorite {
  kind: "tutor";
  tutorId: string;
  source?: string;
  timestamp: number;
}

export interface PendingCourseFavorite {
  kind: "course";
  courseId: string;
  source?: string;
  timestamp: number;
}

export type PendingFavorite = PendingTutorFavorite | PendingCourseFavorite;

/**
 * Store a pending favorite action in sessionStorage
 * @param tutorId - The tutor ID to favorite after login
 * @param source - Optional source tracking parameter
 */
export function setPendingFavorite(tutorId: string, source?: string): void {
  const pendingFavorite: PendingFavorite = {
    kind: "tutor",
    tutorId,
    source,
    timestamp: Date.now(),
  };

  sessionStorage.setItem(PENDING_FAVORITE_KEY, JSON.stringify(pendingFavorite));
}

export function setPendingCourseFavorite(courseId: string, source?: string): void {
  const pendingFavorite: PendingFavorite = {
    kind: "course",
    courseId,
    source,
    timestamp: Date.now(),
  };

  sessionStorage.setItem(PENDING_FAVORITE_KEY, JSON.stringify(pendingFavorite));
}

function normalizePendingFavorite(value: unknown): PendingFavorite | undefined {
  if (typeof value !== "object" || value == null) {
    return undefined;
  }

  const pendingFavorite = value as Partial<PendingFavorite> & {
    tutorId?: unknown;
    courseId?: unknown;
    source?: unknown;
    timestamp?: unknown;
  };

  if (typeof pendingFavorite.timestamp !== "number") {
    return undefined;
  }

  if (pendingFavorite.kind === "course" && typeof pendingFavorite.courseId === "string") {
    return {
      kind: "course",
      courseId: pendingFavorite.courseId,
      source: typeof pendingFavorite.source === "string" ? pendingFavorite.source : undefined,
      timestamp: pendingFavorite.timestamp,
    };
  }

  if (
    (pendingFavorite.kind === "tutor" || pendingFavorite.kind == null) &&
    typeof pendingFavorite.tutorId === "string"
  ) {
    return {
      kind: "tutor",
      tutorId: pendingFavorite.tutorId,
      source: typeof pendingFavorite.source === "string" ? pendingFavorite.source : undefined,
      timestamp: pendingFavorite.timestamp,
    };
  }

  return undefined;
}

/**
 * Retrieve a pending favorite action from sessionStorage
 * Returns undefined if no pending favorite exists or if it has expired
 */
export function getPendingFavorite(): PendingFavorite | undefined {
  const item = sessionStorage.getItem(PENDING_FAVORITE_KEY);
  if (!item) {
    return undefined;
  }

  try {
    const pendingFavorite = normalizePendingFavorite(JSON.parse(item));
    if (!pendingFavorite) {
      clearPendingFavorite();
      return undefined;
    }

    // Check if the pending favorite has expired
    const isExpired = Date.now() - pendingFavorite.timestamp > EXPIRATION_TIME_MS;
    if (isExpired) {
      clearPendingFavorite();
      return undefined;
    }

    return pendingFavorite;
  } catch (error) {
    // JSON.parse throws SyntaxError for invalid JSON
    if (error instanceof SyntaxError) {
      clearPendingFavorite();
      return undefined;
    }
    // Rethrow unexpected errors
    throw error;
  }
}

/**
 * Remove the pending favorite action from sessionStorage
 *
 * Note: This only clears the pending favorite in the current tab. Cross-tab
 * synchronization is not implemented. If a user logs out in one tab, other tabs
 * will not be notified to clear their pending favorites. This is acceptable for
 * Phase 1 since:
 * - sessionStorage is already tab-isolated by design
 * - clearSessionQueryData() (called on logout) uses queryClient.clear() which only
 *   affects the current tab's React Query cache
 * - The pending favorite will expire after 15 minutes regardless
 * - Most users complete login flows within a single tab
 *
 * Future enhancement: Consider adding a storage event listener to sync clears
 * across tabs if multi-tab synchronization becomes a requirement.
 */
export function clearPendingFavorite(): void {
  sessionStorage.removeItem(PENDING_FAVORITE_KEY);
}

/**
 * Check if a pending favorite action exists in sessionStorage
 */
export function hasPendingFavorite(): boolean {
  return getPendingFavorite() !== undefined;
}
